/**
 * Registration complete: the page Stripe returns a buyer to.
 *
 * ── WHAT IT ANSWERS ───────────────────────────────────────────────────────
 * Someone has just paid. They want three things, in this order: did it work,
 * where is the dashboard, and how do students join. The Worker answers all
 * three at `GET /api/enterprise/checkout/complete`, by the Checkout Session id
 * Stripe writes into this address (Sep 14 2026), and it fulfils the checkout
 * itself when this page arrives before Stripe's webhook does. The page
 * decides nothing about money. It reads, and says what it read.
 *
 * ── THE TWO WAITS ─────────────────────────────────────────────────────────
 * `preparing` means the Worker could not reach Stripe to confirm the payment
 * yet. The page asks again every three seconds, twenty times, then stops and
 * says the owner is emailed when the workspace is ready, which the webhook's
 * processor does when it lands.
 *
 * `ready` with no join kit is the normal paid state, not a fault. A card
 * tenant starts without a code: P3 connects the institution to the student
 * app by hand within one business day (Thomas, Sep 14 2026: one click in
 * Retool). The page says so, and asks twice a minute for ten minutes, so a
 * buyer who keeps the tab open sees the code arrive.
 *
 * ── WHAT A FAILED READ SAYS ───────────────────────────────────────────────
 * After the page has shown something, a failed read keeps what it shows and
 * the wait carries on. A FIRST read that fails says the page could not load
 * the checkout and offers to try again. It never says the checkout does not
 * exist: only the Worker says that, and someone who has just paid must not
 * hear it from a dropped connection.
 *
 * Every sentence and every interval lives in checkout-model.ts, where
 * tests/checkout.test.ts reads them.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

/* The terminal screens' classes live in the checkout layer. Checkout.tsx
   imports it too; importing it here keeps this page whole on its own. */
import "~/app/styles/checkout.css";

import {
  COMPLETION_COPY,
  clearCheckoutDraft,
  completionFromResponse,
  nextCompletionPoll,
  readSessionId,
  registeredHeadline,
  type CheckoutCompletion,
  type CompletionRead,
} from "~/app/components/checkout/checkout-model";
import { GatewayShell, LOGIN_PATH } from "~/app/components/GatewayShell";
import { Button, buttonClass } from "~/app/components/primitives";
import { request } from "~/app/lib/api";
import { cn } from "~/app/lib/cn";
import { LEGAL } from "~/app/lib/pricing";
import { TestModeBanner } from "~/app/pages/Checkout";

/* ==========================================================================
   Props and the view
   ========================================================================== */

export interface CheckoutCompleteProps {
  /** The query string holding `session_id`. Unset, the page's own address. */
  search?: string;
  /** One read of the completion. Unset, the Worker is asked; a story passes its own. */
  load?: (sessionId: string) => Promise<CompletionRead>;
  chrome?: boolean;
  className?: string;
}

export type CompletionView =
  | { readonly kind: "loading" }
  | { readonly kind: "unreachable" }
  | {
      readonly kind: "shown";
      readonly completion: CheckoutCompletion;
      /** True once the page has stopped asking. Only `preparing` says anything about it. */
      readonly stoppedAsking: boolean;
    };

const UNKNOWN_VIEW: CompletionView = {
  kind: "shown",
  completion: { state: "unknown" },
  stoppedAsking: true,
};

/* ==========================================================================
   The page
   ========================================================================== */

export function CheckoutComplete({ search, load, chrome = true, className }: CheckoutCompleteProps) {
  const sessionId = useMemo(
    () => readSessionId(search ?? (typeof window === "undefined" ? "" : window.location.search)),
    [search],
  );
  const { view, retry } = useCompletion(sessionId, load ?? askWorker);

  /* Paid: the details checkout kept for a return from Stripe are no longer
     needed (checkout-model section 14). An unpaid answer keeps them, since
     that buyer may go back and try again. */
  const paid = view.kind === "shown" && view.completion.state === "ready";
  useEffect(() => {
    if (!paid) return;
    try {
      clearCheckoutDraft(typeof window === "undefined" ? null : window.sessionStorage);
    } catch {
      /* storage refused: nothing was kept */
    }
  }, [paid]);

  return (
    <GatewayShell
      current="checkout"
      chrome={chrome}
      mainId="checkout-main"
      skipLabel="Skip to your registration"
      className={cn("eg-co-page", className)}
    >
      {/* One live region that outlives every state, so the change from
          "Checking" to what the Worker said is announced. */}
      <div aria-live="polite">
        <CompletionContent view={view} onRetry={retry} />
      </div>
    </GatewayShell>
  );
}

/**
 * Reads the completion, then keeps reading while the answer is one that
 * changes on its own. The load function is held in a ref, so a story that
 * passes a new function on every render does not restart the wait.
 */
function useCompletion(
  sessionId: string | null,
  load: (sessionId: string) => Promise<CompletionRead>,
): { view: CompletionView; retry: () => void } {
  const [view, setView] = useState<CompletionView>(() =>
    sessionId === null ? UNKNOWN_VIEW : { kind: "loading" },
  );
  const [round, setRound] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    // No id, or not an id's shape: nothing is sent, and it reads as unknown.
    if (sessionId === null) {
      setView(UNKNOWN_VIEW);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let shown: CheckoutCompletion | null = null;
    let preparingTries = 0;
    let joinWaitStarted: number | null = null;

    const read = async (): Promise<void> => {
      let result: CompletionRead;
      try {
        result = await loadRef.current(sessionId);
      } catch {
        result = { kind: "failed" };
      }
      if (cancelled) return;

      const completion = result.kind === "read" ? result.completion : shown;
      if (completion === null) {
        setView({ kind: "unreachable" });
        return;
      }
      shown = completion;
      preparingTries = completion.state === "preparing" ? preparingTries + 1 : 0;
      if (completion.state === "ready" && completion.join === null && joinWaitStarted === null) {
        joinWaitStarted = Date.now();
      }

      const delay = nextCompletionPoll(completion, {
        preparingTries,
        joinWaitedMs: joinWaitStarted === null ? 0 : Date.now() - joinWaitStarted,
      });
      setView({ kind: "shown", completion, stoppedAsking: delay === null });
      if (delay !== null) timer = setTimeout(() => void read(), delay);
    };

    void read();
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [sessionId, round]);

  const retry = useCallback(() => {
    setView({ kind: "loading" });
    setRound((n) => n + 1);
  }, []);

  return { view, retry };
}

async function askWorker(sessionId: string): Promise<CompletionRead> {
  const raw = await request(
    `/api/enterprise/checkout/complete?session_id=${encodeURIComponent(sessionId)}`,
  );
  return raw === null ? { kind: "failed" } : completionFromResponse(raw.status, raw.json);
}

/* ==========================================================================
   What each state shows

   Title, one sentence, the action. Exported so a test can render every
   state without a network.
   ========================================================================== */

export function CompletionContent({ view, onRetry }: { view: CompletionView; onRetry: () => void }) {
  if (view.kind === "loading") {
    return <Screen title={COMPLETION_COPY.loading} busy />;
  }

  if (view.kind === "unreachable") {
    const copy = COMPLETION_COPY.unreachable;
    return (
      <Screen title={copy.title}>
        <p className="eg-co-lede">{copy.body}</p>
        {/* Primary LAST, as on every checkout step: `.eg-co-actions` is a
            reversed column on a phone, so last in the source is on top
            there, and on the right on a wide screen. */}
        <div className="eg-co-actions">
          <WriteToP3 />
          <Button size="lg" onClick={onRetry}>
            {copy.action}
          </Button>
        </div>
      </Screen>
    );
  }

  const { completion } = view;
  const testMode = completion.state !== "unknown" && !completion.livemode ? <TestModeBanner /> : null;

  switch (completion.state) {
    case "ready": {
      const copy = COMPLETION_COPY.ready;
      const { join } = completion;
      return (
        <Screen label={copy.label} title={registeredHeadline(completion.institution)} mark>
          <p className="eg-co-lede">{join ? copy.joinLede : copy.joinPending}</p>
          {join ? (
            <p className="eg-co-quoteno">
              <span className="e-label">{copy.codeLabel}</span>
              <strong>{join.code}</strong>
              <a href={join.joinUrl}>{withoutScheme(join.joinUrl)}</a>
            </p>
          ) : null}
          <div className="eg-co-actions">
            <a className={buttonClass("primary", "lg")} href={completion.loginUrl}>
              {copy.dashboard}
            </a>
          </div>
          <p className="eg-co-hint">{copy.signIn}</p>
          {/* Last, so it never parts the sentence above from the code it names. */}
          {testMode}
        </Screen>
      );
    }

    case "preparing": {
      const copy = COMPLETION_COPY.preparing;
      return (
        <Screen title={copy.title}>
          <p className="eg-co-lede">{view.stoppedAsking ? copy.stillBody : copy.body}</p>
          {testMode}
          {view.stoppedAsking ? (
            <div className="eg-co-actions">
              <WriteToP3 />
            </div>
          ) : null}
        </Screen>
      );
    }

    case "unpaid": {
      const copy = COMPLETION_COPY.unpaid;
      return (
        <Screen title={copy.title}>
          {testMode}
          <div className="eg-co-actions">
            <a className={buttonClass("primary", "lg")} href="/checkout">
              {copy.action}
            </a>
          </div>
        </Screen>
      );
    }

    case "expired": {
      const copy = COMPLETION_COPY.expired;
      return (
        <Screen title={copy.title}>
          <p className="eg-co-lede">{copy.body}</p>
          {testMode}
          <div className="eg-co-actions">
            <a className={buttonClass("primary", "lg")} href="/pricing">
              {copy.action}
            </a>
          </div>
        </Screen>
      );
    }

    case "attention": {
      const copy = COMPLETION_COPY.attention;
      return (
        <Screen title={copy.title}>
          <p className="eg-co-lede">{copy.body}</p>
          {testMode}
          <div className="eg-co-actions">
            <WriteToP3 />
          </div>
        </Screen>
      );
    }

    case "unknown": {
      const copy = COMPLETION_COPY.unknown;
      return (
        <Screen title={copy.title}>
          <div className="eg-co-actions">
            <a className={buttonClass("secondary")} href="/pricing">
              {copy.pricing}
            </a>
            <a className={buttonClass("primary", "lg")} href={LOGIN_PATH}>
              {copy.signIn}
            </a>
          </div>
        </Screen>
      );
    }
  }
}

function Screen({
  label = COMPLETION_COPY.label,
  title,
  mark = false,
  busy = false,
  children,
}: {
  label?: string;
  title: string;
  mark?: boolean;
  busy?: boolean;
  children?: ReactNode;
}) {
  return (
    <section className="eg-cc" aria-busy={busy || undefined}>
      <div className="eg-cc-main eg-co-done">
        {mark ? (
          <span className="eg-co-done-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12.5 10 17.5 19 7" />
            </svg>
          </span>
        ) : null}
        <p className="e-label">{label}</p>
        <h1 className="e-h1">{title}</h1>
        {children}
      </div>
      {/* The campus, as the sign-in shows it (Sep 15 2026). Decorative. */}
      <div className="eg-cc-visual" aria-hidden="true">
        <span className="eg-cc-photo" />
        <img className="eg-cc-logo" src="/brand/p3-logo-white.svg" alt="" width="132" height="40" />
      </div>
    </section>
  );
}

function WriteToP3() {
  return (
    <a className={buttonClass("secondary")} href={`mailto:${LEGAL.contactEmail}`}>
      Write to {LEGAL.contactEmail}
    </a>
  );
}

/** The join link as a person reads it aloud: no scheme. The href keeps it. */
function withoutScheme(url: string): string {
  return url.replace(/^https?:\/\//i, "");
}
